/* Registers the SYMIN service worker (production only) with:
   - a one-time "kill switch" that purges any legacy SW + caches (unsticks existing users), and
   - an update flow that surfaces a gold-branded "New version available" banner.
   NOTE: we deliberately never register on the dev/preview server - registering there would
   cache the dev bundle and recreate the very "stuck on old version" bug we are fixing. */

const KILL_SWITCH_VERSION = '1'; // bump this to force a one-time SW + cache reset for everyone
const KILL_KEY = 'symin-sw-reset';

async function runKillSwitchOnce() {
  try {
    if (window.localStorage.getItem(KILL_KEY) === KILL_SWITCH_VERSION) return false;
    let cleared = false;
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const r of regs) { await r.unregister(); cleared = true; }
    if ('caches' in window) {
      const keys = await caches.keys();
      for (const k of keys) { await caches.delete(k); cleared = true; }
    }
    window.localStorage.setItem(KILL_KEY, KILL_SWITCH_VERSION);
    return cleared;
  } catch (e) {
    return false;
  }
}

export function registerServiceWorker({ onUpdate } = {}) {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  if (process.env.NODE_ENV !== 'production') return;

  const start = async () => {
    // Clear any user stuck on a previously broken service worker, then reload once into a clean state.
    const purged = await runKillSwitchOnce();
    if (purged) { window.location.reload(); return; }

    let reg;
    try {
      reg = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
    } catch (e) {
      return; // app works fine without the SW
    }

    // A new worker may already be waiting (previous tab installed it).
    if (reg.waiting && navigator.serviceWorker.controller) onUpdate && onUpdate(reg);

    reg.addEventListener('updatefound', () => {
      const installing = reg.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          onUpdate && onUpdate(reg); // an update is ready (not the first install)
        }
      });
    });

    // Proactively check for a new deploy hourly and whenever the tab regains focus.
    setInterval(() => reg.update().catch(() => {}), 60 * 60 * 1000);
    window.addEventListener('focus', () => reg.update().catch(() => {}));

    // Reload exactly once when the new worker takes control (after the user accepts the update).
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  };

  // Run after load, but also handle the case where the page has already finished loading
  // (e.g. the SW is registered from a React component mounted after the load event).
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}

// Called from the "Refresh" action on the update banner: activate the waiting worker.
export function promptUpdate(reg) {
  if (reg && reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
}
